
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { User } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import UserPanel from './UserPanel';
import { useTasks } from '@/context/TaskContext';
import { useLists } from '@/context/ListContext';

const UserDropdown: React.FC = () => {
  const { user, signOut, profile } = useAuth();
  const { tasks, allTasks } = useTasks();
  const { lists } = useLists();
  const [showUserPanel, setShowUserPanel] = useState(false);

  const handleLogout = async () => {
    try {
      await signOut();
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const getUserDisplayName = () => {
    // Use the name from profile first
    if (profile?.name) {
      return profile.name;
    }
    
    // Fallback to metadata if available
    if (user?.user_metadata?.first_name && user?.user_metadata?.last_name) {
      return `${user.user_metadata.first_name} ${user.user_metadata.last_name}`;
    }
    if (user?.user_metadata?.name) {
      return user.user_metadata.name;
    }
    if (user?.user_metadata?.full_name) {
      return user.user_metadata.full_name;
    }
    
    // Last resort: email prefix (but this should be rare now)
    return user?.email?.split('@')[0] || "User";
  };

  const memberSince = user?.created_at 
    ? new Date(user.created_at).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })
    : 'Unknown';

  // Use active list tasks for current operations, but allTasks for statistics
  const todoTasks = tasks.filter(task => task.status === 'to-do').length;
  const doneTasks = tasks.filter(task => task.status === 'done').length;
  const totalPomodoros = allTasks.reduce((total, task) => total + (task.pomodoros_completed || 0), 0);

  return (
    <>
      <Button 
        onClick={() => setShowUserPanel(true)}
        variant="ghost" 
        size="icon"
        className="bg-black/30 backdrop-blur-sm rounded-full border border-white/30 hover:bg-black/40 h-10 w-10"
        aria-label="User menu"
      >
        {user?.user_metadata?.avatar_url ? (
          <img 
            src={user.user_metadata.avatar_url} 
            alt="User Avatar" 
            className="w-5 h-5 rounded-full object-cover"
            loading="lazy"
            decoding="async"
          />
        ) : (
          <User className="h-5 w-5 text-white" />
        )}
      </Button>

      <UserPanel
        isOpen={showUserPanel}
        onClose={() => setShowUserPanel(false)}
        user={user}
        profile={profile}
        memberSince={memberSince}
        todoTasks={todoTasks}
        doneTasks={doneTasks}
        totalPomodoros={totalPomodoros}
        onLogout={handleLogout}
        lists={lists}
        tasks={allTasks}
      />
    </>
  );
};

export default UserDropdown;
